import { Checkbox, Skeleton, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import SettingsTile from 'components/Billing/SettingsTile';
import {
  EdgeFunctionName,
  ManageEmailNotificationsParams,
  GetEmailNotificationsResponse,
} from 'types/edgeFunctions';

export const EmailNotificationsSettings = () => {
  const [isSaving, setIsSaving] = useState(false);
  // Local overrides that survive background re-fetches
  const [localOverrides, setLocalOverrides] = useState<Partial<GetEmailNotificationsResponse>>({});

  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const syncTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { privateEmail } = useCurrentUser();

  const { toastSuccess, toastError } = useToastWrapper();

  const queryKey = [EdgeFunctionName.GetEmailNotifications, privateEmail];

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<GetEmailNotificationsResponse>(
        EdgeFunctionName.GetEmailNotifications
      );

      if (error) throw error;

      return data;
    },
  });

  // Merged state: server data + local overrides (local wins)
  const isSubscribedToMarketing = localOverrides.isSubscribedToMarketing ?? data?.isSubscribedToMarketing ?? false;
  const isSubscribedToBiWeeklyNewsletter = localOverrides.isSubscribedToBiWeeklyNewsletter ?? data?.isSubscribedToBiWeeklyNewsletter ?? false;

  // Delayed re-fetch to sync with Resend after eventual consistency settles
  const scheduleSync = useCallback(() => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey });
      setLocalOverrides({});
    }, 2000);
  }, [queryClient, queryKey]);

  const handleMarketingChange = async () => {
    if (isSaving) return;

    const newValue = !isSubscribedToMarketing;

    // Immediately reflect in UI via local override
    setLocalOverrides((prev) => ({ ...prev, isSubscribedToMarketing: newValue }));

    try {
      setIsSaving(true);

      const { error } = await supabase.functions.invoke(EdgeFunctionName.ManageEmailNotifications, {
        body: {
          shouldSubscribeToMarketing: newValue,
        } as ManageEmailNotificationsParams,
      });

      if (error) throw error;

      toastSuccess('Marketing email preferences updated');
      scheduleSync();
    } catch (error: any) {
      // Revert on failure
      setLocalOverrides((prev) => ({ ...prev, isSubscribedToMarketing: !newValue }));
      toastError('Failed to update marketing preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBiweeklyNewsletterChange = async () => {
    if (isSaving) return;

    const newValue = !isSubscribedToBiWeeklyNewsletter;

    // Immediately reflect in UI via local override
    setLocalOverrides((prev) => ({ ...prev, isSubscribedToBiWeeklyNewsletter: newValue }));

    try {
      setIsSaving(true);

      const { error } = await supabase.functions.invoke(EdgeFunctionName.ManageEmailNotifications, {
        body: {
          shouldSubscribeToBiWeeklyNewsletter: newValue,
        } as ManageEmailNotificationsParams,
      });

      if (error) throw error;

      toastSuccess('Newsletter email preferences updated');
      scheduleSync();
    } catch (error: any) {
      // Revert on failure
      setLocalOverrides((prev) => ({ ...prev, isSubscribedToBiWeeklyNewsletter: !newValue }));
      toastError('Failed to update newsletter preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SettingsTile title="Email notifications" subtitle="Stay informed and never miss an update!">
        {isPending ? (
          <VStack>
            <Skeleton width="100%" height="24px" />
            <Skeleton width="100%" height="24px" />
          </VStack>
        ) : (
          <VStack alignItems="flex-start">
            <Checkbox
              isChecked={isSubscribedToMarketing}
              onChange={handleMarketingChange}
              isDisabled={isSaving}
            >
              Receive promotional offers & product updates
            </Checkbox>
            <Checkbox
              isChecked={isSubscribedToBiWeeklyNewsletter}
              onChange={handleBiweeklyNewsletterChange}
              isDisabled={isSaving}
            >
              Subscribe to LevelUp newsletter
            </Checkbox>
          </VStack>
        )}
      </SettingsTile>
    </>
  );
};
